import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, AlertCircle, Watch } from 'lucide-react';
import { InfoTooltip } from '@/components/shared';
import { Minus } from 'lucide-react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { READINESS_SCORE_TOOLTIP, COMPONENT_TOOLTIPS } from '@/constants/tooltips';

// Register Chart.js components for Radar chart
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface ReadinessScoreProps {
  athleteId?: number;
  isDark: boolean;
  defaultExpanded?: boolean;
}

interface ReadinessResponse {
  readiness_score: number;
  tier: string;
  confidence_adjusted_score: number;
  score_range: {
    lower: number;
    upper: number;
    center: number;
  };
  components?: {
    hrv?: {
      score?: number;
      z_score?: number;
      composite_score: number;
      raw_value?: number;  // HRV in ms (new API)
      baseline_mean?: number;  // HRV baseline (old API)
      unit?: string;
      trend?: number;
      weekly_trend?: number | null;  // 7d avg vs prior 7d (null if < 14 days)
      is_estimated?: boolean;  // True if HRV is estimated from BPM (less accurate)
    };
    // RHR can be a number (old API) or object (new API)
    rhr?: number | {
      score: number;
      raw_value: number;  // RHR in bpm
      unit: string;
      trend: number;
      weekly_trend?: number | null;
    };
    sleep?: {
      final_score: number;
      duration_score?: number;
      consistency_penalty?: number;
      raw_value?: number;  // Sleep hours (new API)
      avg_duration?: number;  // Sleep hours (old API)
      unit?: string;
      trend?: number;
      weekly_trend?: number | null;
    };
    acwr?: {
      score: number;
      raw_value?: number;  // ACWR ratio
      unit?: string;
      acute_load?: number;
      chronic_load?: number;
      zone?: string;
    } | null;
    wellness?: {
      composite_score: number;
      hooper_index?: number;
    } | null;
  };
  device_confidence?: {
    overall_confidence: number;
    device_type: string;
  };
  date: string;
  warnings: string[];
}

interface MetricData {
  name: string;
  rawValue: number;
  unit: string;
  score: number;
  trend: number;
  weeklyTrend?: number | null;  // 7d directional trend (null if < 14 days)
  available: boolean;
  isEstimated?: boolean;  // True if value is estimated (e.g., HRV from BPM)
}


const ReadinessScore: React.FC<ReadinessScoreProps> = ({ athleteId, isDark, defaultExpanded = false }) => {
  const [readinessData, setReadinessData] = useState<ReadinessResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  // Fetch readiness data (trends now come from API)
  useEffect(() => {
    const fetchReadiness = async (refresh: boolean = false): Promise<ReadinessResponse | null> => {
      const qs = `days=14&include_details=true${refresh ? '&refresh=true' : ''}`;
      const url = athleteId
        ? getApiUrl(`/api/readiness/${athleteId}/?${qs}`)
        : getApiUrl(`/api/readiness/?${qs}`);

      const result = await authenticatedFetchWithRetry<ReadinessResponse>(
        url,
        { method: 'GET' },
        'fetching readiness data',
        2
      );

      if (!result.success) {
        if (result.statusCode === 400 && result.error?.includes('connection')) {
          return null;
        }
        throw new Error(result.error || 'Failed to fetch readiness score');
      }

      // Backend returns 200 with status: "no_data" when no wearable data exists yet
      if (result.data && (result.data as any).status === 'no_data') {
        return null;
      }

      return result.data;
    };

    const triggerSyncAndRetry = async (): Promise<ReadinessResponse | null> => {
      const token = localStorage.getItem('access');
      if (!token) return null;

      try {
        await fetch(getApiUrl('/api/wearables/sync/'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Wait for Celery sync task to complete, then recompute readiness
        await new Promise(resolve => setTimeout(resolve, 6000));
        return await fetchReadiness(true);
      } catch {
        return null; // Silent failure — stale/no data is still usable
      }
    };

    const fetchReadinessData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fast path: load cached readiness immediately
        const cached = await fetchReadiness(false);
        setReadinessData(cached);
        setLoading(false);

        // Only auto-sync on the user's own dashboard (not when viewing another athlete)
        if (!athleteId) {
          const today = new Date().toISOString().split('T')[0];
          const needsSync =
            !cached ||                                      // No data yet (new connection)
            (cached.date && cached.date !== today);          // Stale cached data (not from today)

          if (needsSync) {
            const fresh = await triggerSyncAndRetry();
            if (fresh) {
              setReadinessData(fresh);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching readiness score:', err);
        setError('Failed to fetch readiness score');
        setLoading(false);
      }
    };

    fetchReadinessData();
  }, [athleteId]);

  // Tier naming/colors aligned with TeamReadinessAssessment
  const getReadinessTier = (score: number): { label: string; color: string } => {
    if (score >= 80) return { label: 'Optimal', color: 'text-green-500' };
    if (score >= 60) return { label: 'Moderate', color: 'text-yellow-400' };
    if (score >= 40) return { label: 'Poor', color: 'text-red-500' };
    return { label: 'Low', color: 'text-red-500' };
  };

  // Get score color based on value
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 65) return 'text-yellow-500';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  // Get readiness label based on score
  const getReadinessLabel = (score: number) => {
    if (score >= 80) return 'Ready to Train';
    if (score >= 65) return 'Train Smart';
    if (score >= 50) return 'Light Activity';
    return 'Recovery Priority';
  };

  // Calculate overall trend from metric trends
  const calculateOverallTrend = (metrics: MetricData[]): number => {
    if (metrics.length === 0) return 0;
    const trendsWithValues = metrics.filter(m => m.trend !== 0);
    if (trendsWithValues.length === 0) return 0;
    return trendsWithValues.reduce((sum, m) => sum + m.trend, 0) / trendsWithValues.length;
  };

  // Extract metrics with raw values and trends from API
  // Backward compatible: works with both old API (scores only) and new API (raw values + trends)
  const getMetrics = (): MetricData[] => {
    if (!readinessData?.components) {
      return [];
    }

    const metrics: MetricData[] = [];
    const current = readinessData.components;

    // HRV - prefer raw_value if available (check for undefined, not falsy since 0 is valid)
    if (current.hrv && current.hrv.composite_score !== undefined) {
      const hasRawValue = current.hrv.raw_value !== undefined && current.hrv.raw_value !== null && current.hrv.raw_value > 0;
      const hrvRaw = hasRawValue ? current.hrv.raw_value : current.hrv.composite_score;
      metrics.push({
        name: 'HRV',
        rawValue: hrvRaw,
        unit: hasRawValue ? (current.hrv.unit || 'ms') : '',
        score: current.hrv.composite_score,
        trend: current.hrv.trend ?? 0,
        weeklyTrend: current.hrv.weekly_trend ?? null,
        available: true,
        isEstimated: current.hrv.is_estimated ?? false,  // Estimated from BPM if no Garmin HRV data
      });
    }

    // RHR - handle both old format (number) and new format (object)
    if (current.rhr !== undefined && current.rhr !== null) {
      const isOldFormat = typeof current.rhr === 'number';
      if (isOldFormat) {
        // Old API: rhr is just a score number
        metrics.push({
          name: 'RHR',
          rawValue: current.rhr as number,
          unit: '',
          score: current.rhr as number,
          trend: 0,
          available: true,
        });
      } else {
        // New API: rhr is an object with raw_value
        const rhrObj = current.rhr as { score: number; raw_value?: number; unit?: string; trend?: number; weekly_trend?: number | null };
        const hasRawValue = rhrObj.raw_value !== undefined && rhrObj.raw_value !== null && rhrObj.raw_value > 0;
        metrics.push({
          name: 'RHR',
          rawValue: hasRawValue ? rhrObj.raw_value : rhrObj.score,
          unit: hasRawValue ? (rhrObj.unit || 'bpm') : '',
          score: rhrObj.score,
          trend: rhrObj.trend ?? 0,
          weeklyTrend: rhrObj.weekly_trend ?? null,
          available: true,
        });
      }
    }

    // Sleep - prefer raw_value (hours), check for undefined not falsy
    if (current.sleep && current.sleep.final_score !== undefined) {
      const hasRawValue = current.sleep.raw_value !== undefined && current.sleep.raw_value !== null && current.sleep.raw_value > 0;
      const sleepRaw = hasRawValue ? current.sleep.raw_value : current.sleep.final_score;
      metrics.push({
        name: 'Sleep',
        rawValue: sleepRaw,
        unit: hasRawValue ? (current.sleep.unit || 'hrs') : '',
        score: current.sleep.final_score,
        trend: current.sleep.trend ?? 0,
        weeklyTrend: current.sleep.weekly_trend ?? null,
        available: true,
      });
    }

    // ACWR - raw ratio value (only if available)
    if (current.acwr && current.acwr.score !== undefined) {
      const hasRawValue = current.acwr.raw_value !== undefined && current.acwr.raw_value !== null;
      metrics.push({
        name: 'ACWR',
        rawValue: hasRawValue ? current.acwr.raw_value : current.acwr.score,
        unit: hasRawValue ? 'ratio' : '',
        score: current.acwr.score,
        trend: 0,
        available: true,
      });
    }

    // Wellness (only if available)
    if (current.wellness && current.wellness.composite_score) {
      metrics.push({
        name: 'Wellness',
        rawValue: current.wellness.composite_score,
        unit: '',
        score: current.wellness.composite_score,
        trend: 0,
        available: true,
      });
    }

    return metrics;
  };

  // Get lowest metric for warning (based on score, not raw value)
  const getLowestMetric = (metrics: MetricData[]): MetricData | null => {
    if (metrics.length === 0) return null;
    return metrics.reduce((lowest, current) =>
      current.score < lowest.score ? current : lowest
    );
  };

  // Get actionable insight for lowest metric
  const getActionableInsight = (metric: MetricData | null): string => {
    if (!metric) return '';

    switch (metric.name) {
      case 'HRV':
        return metric.score < 50
          ? 'Focus on stress reduction and quality sleep tonight'
          : 'Consider light recovery activities today';
      case 'RHR':
        return metric.score < 50
          ? 'Your resting heart rate indicates fatigue - prioritize rest'
          : 'Monitor hydration and avoid high-intensity training';
      case 'Sleep':
        return metric.score < 50
          ? 'Prioritize 8+ hours of sleep and improve sleep hygiene'
          : 'Try going to bed 30 minutes earlier tonight';
      case 'ACWR':
        return metric.score < 50
          ? 'Training load imbalance detected - reduce intensity'
          : 'Gradually increase training volume this week';
      case 'Wellness':
        return metric.score < 50
          ? 'Focus on recovery: nutrition, hydration, and rest'
          : 'Check in on mood and stress levels';
      default:
        return 'Focus on recovery and monitor your metrics';
    }
  };


  // Radar chart data - uses scores (normalized 0-100) for proper visualization
  const getRadarChartData = (metrics: MetricData[]) => {
    return {
      labels: metrics.map(m => m.name),
      datasets: [
        {
          label: 'Score',
          data: metrics.map(m => m.score),
          backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(37, 99, 235, 0.12)',
          borderColor: isDark ? '#3B82F6' : '#2563EB',
          borderWidth: 2,
          pointBackgroundColor: isDark ? '#60A5FA' : '#3B82F6',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: isDark ? '#60A5FA' : '#3B82F6',
        },
      ],
    };
  };

  // Radar chart options
  const getRadarChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          color: isDark ? '#374151' : '#9CA3AF',
        },
        grid: {
          color: isDark ? '#374151' : '#9CA3AF',
        },
        pointLabels: {
          color: isDark ? '#9CA3AF' : '#1F2937',
          font: {
            size: 12,
          },
        },
        ticks: {
          color: isDark ? '#6B7280' : '#374151',
          backdropColor: 'transparent',
          font: {
            size: 10,
          },
          stepSize: 20,
        },
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
        titleColor: isDark ? '#F9FAFB' : '#111827',
        bodyColor: isDark ? '#D1D5DB' : '#374151',
        borderColor: isDark ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => ` ${context.label}: ${context.raw.toFixed(1)}`,
        },
      },
    },
  });

  // Loading state
  if (loading) {
    return (
      <div className={`cgm-metric-card rounded-2xl p-6`}>
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3"></div>
            <p className="cgm-text-secondary text-sm">Loading readiness score...</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - no data or no device connection
  if (!readinessData && !loading && !error) {
    return (
      <div className="cgm-metric-card rounded-2xl p-8">
        <div className="text-center space-y-6">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-500/10">
            <Watch className="w-10 h-10 text-blue-500" />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">No Readiness Data Yet</h3>
            <p className="theme-text-secondary text-base max-w-md mx-auto leading-relaxed">
              Connect your Whoop or Garmin device to start tracking your readiness score and get personalized recovery insights based on your HRV, sleep, and training load.
            </p>
          </div>

          {/* CTA Button */}
          <Link
            to="/connect-wearable"
            className="inline-flex items-center space-x-2 cgm-button px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
          >
            <Watch className="w-5 h-5" />
            <span>Connect Device</span>
          </Link>
        </div>
      </div>
    );
  }

  // Error state - only for actual errors
  if (error) {
    return (
      <div className={`cgm-metric-card rounded-2xl p-6`}>
        <div className="flex items-center justify-center h-32">
          <div className="text-center space-y-4">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div>
              <p className="text-red-500 text-sm font-medium mb-2">{error}</p>
              <Link
                to="/connect-wearable"
                className="text-blue-500 hover:text-blue-600 text-sm font-medium underline"
              >
                Check device connection
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!readinessData) {
    return null;
  }

  const metrics = getMetrics();
  const lowestMetric = getLowestMetric(metrics);
  const overallTrend = calculateOverallTrend(metrics);
  const readinessTier = getReadinessTier(readinessData.readiness_score);

  return (
    <div
      className={`cgm-metric-card rounded-2xl p-6 cursor-pointer transition-all duration-300 ease-in-out border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
      onClick={() => setIsExpanded(!isExpanded)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }
      }}
      tabIndex={0}
      role="button"
      aria-expanded={isExpanded}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center gap-1.5">
            <h3 className="text-lg font-bold cgm-text-primary">Today's Readiness</h3>
            <InfoTooltip
              title={READINESS_SCORE_TOOLTIP.title}
              body={READINESS_SCORE_TOOLTIP.body}
              isDark={isDark}
              width="w-80"
              iconSize={4}
              ariaLabel="How Readiness Score is calculated"
            />
          </div>
          {overallTrend !== 0 && (
            <div className={`flex items-center space-x-1 ${overallTrend > 0 ? 'text-green-500' : overallTrend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
              {overallTrend > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : overallTrend < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <Minus className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">
                {overallTrend > 0 ? '+' : ''}{overallTrend.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        <button
          className="text-gray-400 hover:text-gray-200 transition-colors p-1"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Two-Column Layout: Spider Chart (Left) + Score & Details (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Spider Chart */}
        {metrics.length > 0 && (
          <div className={`rounded-xl p-4 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
            <h4 className="text-sm font-semibold cgm-text-secondary mb-3">Readiness Breakdown</h4>
            <div className="h-56">
              <Radar data={getRadarChartData(metrics)} options={getRadarChartOptions()} />
            </div>
          </div>
        )}

        {/* Right Column - Score Display & Component Details */}
        <div className="space-y-4">
          {/* Score Display */}
          <div className={`rounded-xl p-6 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
            <div className="text-sm mb-1 cgm-text-secondary">Overall Readiness</div>
            <div className="text-5xl font-bold mb-2 cgm-text-primary">
              {readinessData.readiness_score.toFixed(0)}%
            </div>
            <div className={`text-lg font-semibold ${readinessTier.color}`}>
              {readinessTier.label}
            </div>
            <div className="text-xs cgm-text-secondary mt-2">
              {getReadinessLabel(readinessData.readiness_score)}
            </div>
          </div>

          {/* Lowest Metric Warning */}
          {lowestMetric && lowestMetric.score < 70 && (
            <div className={`flex items-start gap-3 p-3 rounded-xl ${isDark ? 'bg-amber-900/20 border-amber-800/30' : 'bg-amber-50 border-amber-200'} border`}>
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  Watch: {lowestMetric.name} {lowestMetric.unit ? `(${lowestMetric.rawValue.toFixed(1)}${lowestMetric.unit})` : `(Score: ${lowestMetric.score.toFixed(0)})`}
                </span>
                <span className={`text-xs ${isDark ? 'text-amber-200/80' : 'text-amber-700'}`}>
                  {getActionableInsight(lowestMetric)}
                </span>
              </div>
            </div>
          )}

          {/* Expanded Content - Component Details */}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-[600px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
            }`}
          >
            {/* Metrics List - Raw Values with Trends */}
            <div>
              <h4 className="text-sm font-semibold cgm-text-secondary mb-3">Component Details</h4>
              {metrics.length > 0 ? (
                <div className="space-y-2">
                  {metrics.map((metric) => {
                    // Determine display value and unit
                    const hasRawValue = metric.unit && metric.unit.length > 0;
                    const displayValue = hasRawValue
                      ? (metric.name === 'ACWR' ? metric.rawValue.toFixed(2) : metric.rawValue.toFixed(metric.unit === 'bpm' ? 0 : 1))
                      : metric.rawValue.toFixed(1);
                    const displayUnit = hasRawValue ? metric.unit : '';

                    const tooltip = COMPONENT_TOOLTIPS[metric.name];
                    return (
                      <div
                        key={metric.name}
                        className={`group/metric relative flex items-center justify-between p-3 rounded-xl border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
                      >
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs cgm-text-secondary tracking-wide flex items-center gap-1.5">
                            {metric.name}
                            {metric.isEstimated && (
                              <span
                                className="text-amber-500 cursor-help normal-case"
                                title="Estimated from heart rate data (overnight HRV unavailable)"
                              >
                                ~
                              </span>
                            )}
                            {tooltip && (
                              <InfoTooltip
                                title={tooltip.title}
                                body={tooltip.body}
                                isDark={isDark}
                                ariaLabel={`How ${metric.name} is calculated`}
                              />
                            )}
                          </span>
                          <div className="flex items-baseline gap-1 mt-0.5">
                            <span className={`text-lg font-bold ${metric.isEstimated ? (isDark ? 'text-amber-400/80' : 'text-amber-600/80') : (isDark ? 'text-white' : 'text-gray-900')}`}>
                              {displayValue}
                            </span>
                            {displayUnit && <span className="text-xs cgm-text-secondary">{displayUnit}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {metric.score.toFixed(0)}/100
                          </span>
                          {/* Primary trend: today vs baseline */}
                          {metric.trend !== 0 ? (
                            <div className={`flex items-center gap-0.5 ${metric.trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {metric.trend > 0 ? (
                                <TrendingUp className="w-3.5 h-3.5" />
                              ) : (
                                <TrendingDown className="w-3.5 h-3.5" />
                              )}
                              <span className="text-xs font-medium">
                                {metric.trend > 0 ? '+' : ''}{metric.trend.toFixed(1)}%
                              </span>
                            </div>
                          ) : (
                            <Minus className="w-3.5 h-3.5 text-gray-400" />
                          )}
                          {/* Weekly trend: 7d avg vs prior 7d */}
                          {metric.weeklyTrend != null && metric.weeklyTrend !== 0 && (
                            <div
                              className={`flex items-center gap-0.5 ${metric.weeklyTrend > 0 ? 'text-green-500/70' : 'text-red-500/70'}`}
                              title={`7-day trend: ${metric.weeklyTrend > 0 ? '+' : ''}${metric.weeklyTrend.toFixed(1)}%`}
                            >
                              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>7d</span>
                              {metric.weeklyTrend > 0 ? (
                                <TrendingUp className="w-3 h-3" />
                              ) : (
                                <TrendingDown className="w-3 h-3" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`p-3 rounded-xl text-center border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
                  <p className="text-sm cgm-text-secondary">
                    No detailed metrics available.
                  </p>
                </div>
              )}
            </div>

            {/* Data Attribution */}
            {readinessData.device_confidence && (
              <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end">
                <div className="text-xs cgm-text-secondary">
                  Data powered by <span className="font-semibold capitalize">{readinessData.device_confidence.device_type === 'whoop' ? 'WHOOP' : readinessData.device_confidence.device_type}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warnings - ALWAYS visible when present (outside collapsible) */}
      {readinessData.warnings && readinessData.warnings.length > 0 && (
        <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-amber-950/40 border-amber-700/50' : 'bg-amber-50 border-amber-300'} border`}>
          <p className={`text-sm font-semibold ${isDark ? 'text-amber-400' : 'text-amber-800'} mb-2 flex items-center gap-2`}>
            <AlertCircle className="w-4 h-4" />
            Data Warnings
          </p>
          <ul className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-700'} space-y-1 ml-6`}>
            {readinessData.warnings.map((warning: string, index: number) => (
              <li key={index} className="flex items-start gap-2">
                <span className={`${isDark ? 'text-amber-400' : 'text-amber-600'} mt-0.5`}>•</span>
                <span>{warning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
        <p className="text-xs cgm-text-secondary text-center">
          Updated: {new Date(readinessData.date).toLocaleDateString()} |
          {isExpanded ? ' Click to collapse' : ' Click to expand details'}
        </p>
      </div>
    </div>
  );
};

export default ReadinessScore;
